/**
 * Campanhas de WhatsApp (v10 Etapa 2) — envio em massa de template HSM com
 * params POR destinatário. Endpoints admin/integrations.manage; contratos do
 * @erp/shared (WaCampaign*, CAMPAIGN_STATUS_LABELS). O detalhe traz o
 * PROGRESSO vivo (agregação por status), não a lista de destinatários.
 */
import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import type { CampaignCreateBody, WaCampaign, WaCampaignDetail, WaCampaignStatus } from '@erp/shared';
import { CAMPAIGN_STATUS_LABELS } from '@erp/shared';
import { api } from '@/lib/api-client';
import { errorMessage as whatsappErrorMessage } from '@/features/whatsapp/whatsapp-queries';

export const CAMPAIGNS_KEY = ['erp', 'whatsapp', 'campaigns'] as const;

// ── Status: rótulo pt-BR (fonte @erp/shared) + variante do chip ─────────────

const STATUS_VARIANT: Record<WaCampaignStatus, 'default' | 'success' | 'warning' | 'info'> = {
  draft: 'default',
  running: 'info',
  paused: 'warning',
  done: 'success',
  canceled: 'default',
};

export function campaignStatusLabel(status: WaCampaignStatus): string {
  return CAMPAIGN_STATUS_LABELS[status] ?? status;
}

export function campaignStatusVariant(
  status: WaCampaignStatus
): 'default' | 'success' | 'warning' | 'info' {
  return STATUS_VARIANT[status] ?? 'default';
}

// ── Queries ─────────────────────────────────────────────────────────────────

export function campaignsOptions() {
  return queryOptions({
    queryKey: CAMPAIGNS_KEY,
    queryFn: async ({ signal }) => {
      const res = await api<WaCampaign[]>('/whatsapp/campaigns', { signal });
      if (res.error) throw res.error;
      return res.data;
    },
    // Progresso vivo: enquanto alguma campanha roda, refaz a cada 5s.
    refetchInterval: (query) =>
      query.state.data?.some((c) => c.status === 'running') ? 5_000 : false,
  });
}

export function campaignDetailOptions(id: number) {
  return queryOptions({
    queryKey: [...CAMPAIGNS_KEY, id] as const,
    queryFn: async ({ signal }) => {
      const res = await api<WaCampaignDetail>(`/whatsapp/campaigns/${id}`, { signal });
      if (res.error) throw res.error;
      return res.data;
    },
    refetchInterval: (query) => (query.state.data?.status === 'running' ? 5_000 : false),
  });
}

// ── Mutations ───────────────────────────────────────────────────────────────

export function useCreateCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CampaignCreateBody) => {
      const res = await api<WaCampaign>('/whatsapp/campaigns', { method: 'POST', body });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: CAMPAIGNS_KEY }),
  });
}

export type CampaignAction = 'start' | 'pause' | 'cancel';

export function useCampaignAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, action }: { id: number; action: CampaignAction }) => {
      const res = await api<WaCampaign>(`/whatsapp/campaigns/${id}/${action}`, {
        method: 'POST',
      });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: CAMPAIGNS_KEY }),
  });
}

export function useDeleteCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const res = await api<void>(`/whatsapp/campaigns/${id}`, { method: 'DELETE' });
      if (res.error) throw res.error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: CAMPAIGNS_KEY }),
  });
}

// ── Erros pt-BR (códigos reais dos use-cases da API) ────────────────────────

const ERROR_LABELS: Record<string, string> = {
  params_mismatch:
    'O número de parâmetros de um destinatário não confere com o modelo escolhido.',
  too_many_recipients: 'A lista tem destinatários demais para uma única campanha.',
  invalid_phone: 'Há telefone inválido na lista de destinatários.',
  campaign_not_found: 'Campanha não encontrada — recarregue a página e tente de novo.',
  campaign_not_draft: 'Só campanhas em rascunho podem ser excluídas.',
  invalid_transition: 'Esta ação não vale para o status atual da campanha.',
  unknown_channel: 'O canal escolhido não existe mais — recarregue e tente de novo.',
  channel_inactive: 'O canal escolhido está inativo — ative-o em Integrações.',
  // política de 02/10 (spec 025): campanha marcaria cada destinatário no
  // roteador da Blip da PoolTrack — só contato a contato (Nova conversa, disparo)
  campaign_channel_not_supported_blip:
    'Campanha não sai pelo número da Blip — use um número da Meta. Pela Blip, só a Nova conversa e a regra de disparo.',
  template_not_found: 'O modelo escolhido não está aprovado na Meta.',
};

/**
 * Mensagem pt-BR do erro. `details.row` (422 invalid_phone/params_mismatch) é
 * o índice 0-based do destinatário — vira "linha N" 1-based na mensagem.
 */
export function campaignErrorMessage(err: unknown): string {
  if (err !== null && typeof err === 'object' && 'error' in err) {
    const code = (err as { error: unknown }).error;
    if (typeof code === 'string') {
      const label = ERROR_LABELS[code];
      if (label) {
        const details = (err as { details?: unknown }).details;
        const row =
          details !== null && typeof details === 'object' && 'row' in details
            ? (details as { row: unknown }).row
            : undefined;
        return typeof row === 'number' ? `${label} (linha ${row + 1})` : label;
      }
    }
  }
  // fallback: códigos do WhatsApp (whatsapp_not_configured…) + transversais
  return whatsappErrorMessage(err);
}
